import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { BookOpen, ExternalLink, Pencil, Plus, Star, Users } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { CourseStatusBadge, formatLabel, levelLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useCreateCourse, useMyCourses } from '@/features/catalog/api'
import { ApiError } from '@/lib/api'
import { formatNumber, formatRupiah } from '@/lib/format'

export function ManageCoursesPage() {
  const { data, isLoading } = useMyCourses()
  const [createOpen, setCreateOpen] = useState(false)
  const courses = data?.courses ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Kelola Aktivitas Kelas</h1>
          <p className="mt-1 text-muted">Susun kurikulum, atur harga, lalu terbitkan ketika siap.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Buat kelas
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="size-6" />}
            title="Belum ada kelas"
            description="Mulai dari informasi dasar; kurikulum dan harga bisa dilengkapi bertahap sebelum terbit."
            action={<Button onClick={() => setCreateOpen(true)}>Buat kelas pertama</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {courses.map((course) => (
            <Card key={course.id}>
              <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt=""
                    className="h-28 w-full shrink-0 rounded-xl object-cover sm:h-20 sm:w-32"
                  />
                ) : (
                  <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand sm:w-32">
                    <BookOpen className="size-6" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <CourseStatusBadge status={course.status} />
                    <span className="text-xs text-subtle">
                      {levelLabel[course.level]} · {formatLabel[course.format]}
                    </span>
                  </div>
                  <h2 className="mt-1.5 font-bold text-ink">{course.title}</h2>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted">
                    <span className="inline-flex items-center gap-1">
                      <Users className="size-3.5" />
                      {formatNumber(course.enrollment_count)} peserta
                    </span>
                    {course.rating_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Star className="size-3.5 fill-amber-400 text-amber-400" />
                        {course.rating.toFixed(1)} ({course.rating_count})
                      </span>
                    ) : null}
                    <span>{course.lesson_count} materi</span>
                    <span className="font-semibold text-ink">
                      {course.effective_price === 0 ? 'Gratis' : formatRupiah(course.effective_price)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {course.status === 'published' ? (
                    <Link to="/kelas/$slug" params={{ slug: course.slug }}>
                      <Button size="sm" variant="outline">
                        <ExternalLink className="size-4" />
                        Lihat
                      </Button>
                    </Link>
                  ) : null}
                  <Link to="/app/kelola-kelas/$courseId" params={{ courseId: course.id }}>
                    <Button size="sm">
                      <Pencil className="size-4" />
                      Kelola
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <CreateCourseDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

const schema = z.object({
  title: z.string().min(5, 'Judul minimal 5 karakter.').max(140, 'Judul terlalu panjang.'),
  subtitle: z.string().max(200, 'Subjudul terlalu panjang.').optional(),
  description: z.string().max(8000).optional(),
  category: z.string().min(2, 'Kategori wajib diisi.').max(60),
  level: z.string(),
  format: z.string(),
  price: z.coerce.number().min(0, 'Harga tidak boleh negatif.'),
})

type FormValues = z.input<typeof schema>

/**
 * Pembuatan kelas hanya menanyakan yang benar-benar perlu di awal.
 *
 * Sisanya — kurikulum, harga akhir, halaman penjualan — dikerjakan di
 * editor. Formulir panjang di muka adalah alasan paling umum kelas berhenti
 * di tengah jalan (SDLC §8.9: progressive disclosure).
 */
function CreateCourseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createCourse = useCreateCourse()
  const navigate = useNavigate()
  const toast = useToast()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
      category: '',
      level: 'beginner',
      format: 'self_paced',
      price: 0,
    },
  })

  const submit = form.handleSubmit((values) => {
    createCourse.mutate(
      {
        title: values.title,
        subtitle: values.subtitle,
        description: values.description,
        category: values.category.toLowerCase().trim(),
        level: values.level,
        format: values.format,
        price: Number(values.price),
      },
      {
        onSuccess: (data) => {
          toast.success('Kelas dibuat', 'Lanjutkan dengan menyusun kurikulum.')
          onClose()
          form.reset()
          navigate({ to: '/app/kelola-kelas/$courseId', params: { courseId: data.course.id } })
        },
        onError: (error) =>
          toast.error('Gagal membuat kelas', error instanceof ApiError ? error.message : undefined),
      },
    )
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buat kelas baru"
      description="Isi informasi dasarnya dulu — sisanya bisa dilengkapi kemudian."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={createCourse.isPending} onClick={submit}>
            Buat dan lanjutkan
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        <Input
          label="Judul kelas"
          required
          placeholder="Misalnya: SQL untuk Analis Data Pemula"
          error={form.formState.errors.title?.message}
          {...form.register('title')}
        />

        <Input
          label="Subjudul"
          placeholder="Satu kalimat yang menjelaskan hasil belajarnya"
          error={form.formState.errors.subtitle?.message}
          {...form.register('subtitle')}
        />

        <Textarea
          label="Deskripsi"
          rows={4}
          hint="Minimal 60 karakter sebelum kelas dapat diterbitkan."
          placeholder="Siapa yang cocok mengikuti kelas ini, dan apa yang akan mereka bisa lakukan setelahnya?"
          error={form.formState.errors.description?.message}
          {...form.register('description')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Kategori"
            required
            placeholder="data, desain, bisnis…"
            error={form.formState.errors.category?.message}
            {...form.register('category')}
          />
          <Input
            label="Harga (Rupiah)"
            type="number"
            min={0}
            step={1000}
            hint="Isi 0 untuk kelas gratis."
            error={form.formState.errors.price?.message}
            {...form.register('price')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Tingkat" {...form.register('level')}>
            <option value="beginner">Pemula</option>
            <option value="intermediate">Menengah</option>
            <option value="advanced">Lanjutan</option>
          </Select>
          <Select label="Format" {...form.register('format')}>
            <option value="self_paced">Belajar mandiri</option>
            <option value="live">Kelas live</option>
            <option value="hybrid">Mandiri + live</option>
          </Select>
        </div>
      </form>
    </Dialog>
  )
}
